import React, { useCallback, useMemo } from 'react';

import { Button } from '@eika/button';
import { Loader } from '@eika/loader';
import { TransactionList } from '@eika/transaction-list';

import ErrorCardWrapper from '../../../components/ErrorCardWrapper';
import IntersectionTrigger from '../../../components/IntersectionTrigger/IntersectionTrigger';
import { AccountId } from '../../../generated-types';
import { useTransactions, useReservedTransactions } from '../../../hooks/useLoans';
import { useTranslation } from '../../../translations';
import formatTransactions from '../../../utils/transactionsUtil';
import TransactionListSkeleton from './TransactionListSkeleton';
import TransactionListStatus from './TransactionListStatus';

import './Transactions.scss';

interface Props {
    accountId: AccountId | undefined;
}

const Transactions: React.FC<Props> = ({ accountId }) => {
    const translated = useTranslation().transactions;
    const { data: reservations, ...reservedQuery } = useReservedTransactions(accountId);
    const { data: transactionPages, ...transactionQuery } = useTransactions(accountId);

    const transactions = useMemo(
        () =>
            (transactionPages?.pages ?? [])
                .filter((x) => !!x?.bookedTransactions)
                .flatMap((x) => x?.bookedTransactions),
        [transactionPages?.pages],
    );

    const formattedTransactions = useMemo(
        () => formatTransactions(reservations, transactions),
        [reservations, transactions],
    );

    const loadMoreTransactions = useCallback(() => {
        if (!transactionQuery.isFetching && transactionQuery.hasNextPage && !transactionQuery.error) {
            transactionQuery.fetchNextPage();
        }
    }, [transactionQuery]);

    const refresh = useCallback(() => {
        transactionQuery.refetch();
        reservedQuery.refetch();
    }, [transactionQuery, reservedQuery]);

    const isNoContentOrError =
        formattedTransactions.length === 0 && (transactionQuery.isSuccess || transactionQuery.isError);

    const noContent = {
        value: formattedTransactions.length === 0,
        title: translated.noTransactions,
    };

    if (transactionQuery.isLoading || reservedQuery.isLoading) {
        return <TransactionListSkeleton count={4} />;
    }

    if (formattedTransactions.length === 0) {
        return (
            <TransactionListStatus
                refresh={refresh}
                error={transactionQuery.error || reservedQuery.error}
                noContent={noContent}
            />
        );
    }

    return (
        <>
            {!isNoContentOrError && (
                <>
                    <IntersectionTrigger onBottom={loadMoreTransactions}>
                        <TransactionList
                            className="loan-overview-transaction-list"
                            transactions={formattedTransactions.map((transaction) => ({
                                ...transaction,
                                label: transaction.label,
                            }))}
                            reservedText={translated.reserved}
                        />
                    </IntersectionTrigger>
                    {transactionQuery.isFetchingNextPage && (
                        <div className="container">
                            <Loader />
                        </div>
                    )}
                    {transactionQuery.error && (
                        <div className="container">
                            <ErrorCardWrapper exception={transactionQuery.error}>
                                <Button onClick={loadMoreTransactions}>{translated.tryAgain}</Button>
                            </ErrorCardWrapper>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default Transactions;
